import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Form } from 'react-bootstrap';
import { EhrIntegrationSettingsData } from '@/common/types';
import { stringFieldRequired } from '@/common/utilities/formFieldSchemas.ts';
import { useDirtyFieldClass } from '@/common/hooks/useDirtyFieldClass.ts';

const schema = z.object({
  ehrProvider: stringFieldRequired('EHR Provider'),
  patientLoginUrl: z
    .string()
    .min(1, 'Patient Login URL is required')
    .url('Patient Login URL must be a valid URL'),
  providerAppointmentBaseUrl: z
    .string()
    .min(1, 'Provider Appointment Base URL is required')
    .url('Provider Appointment Base URL must be a valid URL'),
});

export type EhrSettingsFormData = z.infer<typeof schema>;

interface EhrSettingsEditProps {
  data: EhrIntegrationSettingsData;
  onSubmit: (formData: Partial<EhrSettingsFormData>) => void;
  submitRef: React.RefObject<(() => void) | null>;
}

const EhrSettingsEdit: React.FC<EhrSettingsEditProps> = ({
  data,
  onSubmit,
  submitRef,
}) => {
  const {
    register,
    getValues,
    formState: { errors, dirtyFields },
  } = useForm<EhrSettingsFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      ehrProvider: data.ehrProvider,
      patientLoginUrl: data.patientLoginUrl,
      providerAppointmentBaseUrl: data.providerAppointmentBaseUrl,
    },
  });

  // Use the dirty field class hook
  const { getDirtyClass, getChangedFields } = useDirtyFieldClass(
    dirtyFields,
    errors,
    getValues
  );

  // Expose the submit function to the parent via ref
  useEffect(() => {
    submitRef.current = () => {
      const changedFields = getChangedFields();
      onSubmit(changedFields);
    };
  }, [getChangedFields, onSubmit, submitRef]);

  return (
    <Form>
      <Form.Group className="mb-3">
        <Form.Label>EHR Provider</Form.Label>
        <Form.Control
          type="text"
          {...register('ehrProvider')}
          isInvalid={!!errors.ehrProvider}
          className={getDirtyClass('ehrProvider')}
        />
        <Form.Control.Feedback type="invalid">
          {errors.ehrProvider?.message}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Patient Login URL</Form.Label>
        <Form.Control
          type="url"
          {...register('patientLoginUrl')}
          isInvalid={!!errors.patientLoginUrl}
          className={getDirtyClass('patientLoginUrl')}
        />
        <Form.Control.Feedback type="invalid">
          {errors.patientLoginUrl?.message}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Provider Appointment Base URL</Form.Label>
        <Form.Control
          type="url"
          {...register('providerAppointmentBaseUrl')}
          isInvalid={!!errors.providerAppointmentBaseUrl}
          className={getDirtyClass('providerAppointmentBaseUrl')}
        />
        <Form.Control.Feedback type="invalid">
          {errors.providerAppointmentBaseUrl?.message}
        </Form.Control.Feedback>
      </Form.Group>
    </Form>
  );
};

export default EhrSettingsEdit;
